import React from "react";
import { Check, Star } from "lucide-react";
import { LANDING } from "@/constants/testIds";

const tiers = [
  {
    id: "starter",
    emoji: "🏀",
    name: "Starter Package",
    price: "$297",
    blurb: "Perfect for businesses that want a quick visibility boost.",
    features: [
      "Visibility Audit",
      "Google Business Review",
      "AI Search Visibility Check",
      "3 Finals Promotional Graphics",
      "3 Social Media Posts",
      "Customer Attraction Recommendations",
    ],
    testId: LANDING.pricingCardStarter,
    ctaTestId: LANDING.pricingCtaStarter,
    highlight: false,
  },
  {
    id: "all_star",
    emoji: "🏀",
    name: "All-Star Package",
    price: "$497",
    blurb: "Everything in Starter — plus the launch firepower.",
    features: [
      "Everything in Starter",
      "5 Finals Promotional Graphics",
      "5 Social Media Posts",
      "Limited-Time Offer Creation",
      "Competitor Visibility Review",
      "Finals Customer Attraction Strategy",
      "AI Search Visibility Recommendations",
    ],
    testId: LANDING.pricingCardAllStar,
    ctaTestId: LANDING.pricingCtaAllStar,
    highlight: true,
  },
  {
    id: "championship",
    emoji: "🏆",
    name: "Championship Package",
    price: "$997",
    blurb: "The full court press for businesses going all-in.",
    features: [
      "Everything in All-Star",
      "Custom Landing Page",
      "Promotional Campaign Setup",
      "Google Business Optimization",
      "AI Search Optimization Recommendations",
      "Strategy Session",
      "Priority Support",
    ],
    testId: LANDING.pricingCardChampionship,
    ctaTestId: LANDING.pricingCtaChampionship,
    highlight: false,
  },
];

const Pricing = ({ onClaim }) => {
  return (
    <section
      data-testid={LANDING.pricingSection}
      className="py-24 md:py-32 px-6 md:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto mb-14 md:mb-20">
          <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#00B2A9] mb-4">
            Pricing
          </p>
          <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
            Choose your{" "}
            <span className="bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-transparent bg-clip-text">
              playbook
            </span>
            .
          </h2>
          <p className="mt-5 text-base md:text-lg text-zinc-500 leading-relaxed">
            One-time payment. Secure Stripe checkout. No subscriptions, no
            applications, no call required.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-7 items-stretch">
          {tiers.map((t) => {
            const inner = (
              <div
                className={`flex flex-col h-full bg-white rounded-3xl p-8 md:p-9 ${
                  t.highlight
                    ? "shadow-[0_20px_60px_-15px_rgba(239,66,111,0.35)]"
                    : "border border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                }`}
              >
                {t.highlight && (
                  <div className="inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-white text-[10px] md:text-xs font-bold uppercase tracking-[0.2em] mb-5">
                    <Star size={12} strokeWidth={3} fill="white" />
                    Most Popular
                  </div>
                )}
                <div className="text-2xl mb-2">{t.emoji}</div>
                <h3 className="text-2xl md:text-3xl font-black tracking-tight">
                  {t.name}
                </h3>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-5xl md:text-6xl font-black tracking-tighter">
                    {t.price}
                  </span>
                  <span className="text-sm text-zinc-500 font-semibold">one-time</span>
                </div>
                <p className="mt-3 text-sm md:text-base text-zinc-500 leading-relaxed">
                  {t.blurb}
                </p>

                <ul className="mt-7 space-y-3 flex-1">
                  {t.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-[#00B2A9]/10 flex items-center justify-center">
                        <Check size={12} strokeWidth={3} className="text-[#00B2A9]" />
                      </span>
                      <span className="text-sm md:text-[15px] text-zinc-700 font-medium leading-snug">
                        {f}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => onClaim(t.id)}
                  data-testid={t.ctaTestId}
                  className={`mt-8 w-full rounded-full font-bold py-4 text-sm tracking-wider uppercase transition-all hover:scale-[1.02] active:scale-[0.98] ${
                    t.highlight
                      ? "bg-gradient-to-r from-[#00B2A9] via-[#EF426F] to-[#FF8200] text-white shadow-lg shadow-black/10 hover:opacity-95"
                      : "bg-[#0A0A0A] text-white hover:shadow-[0_10px_40px_-10px_#0A0A0A]"
                  }`}
                >
                  🏀 Claim My Finals Spot
                </button>
              </div>
            );

            return t.highlight ? (
              <div
                key={t.id}
                data-testid={t.testId}
                className="relative rounded-[1.6rem] lg:scale-[1.03] lg:-my-2 p-[2px] bg-gradient-to-br from-[#00B2A9] via-[#EF426F] to-[#FF8200]"
              >
                {inner}
              </div>
            ) : (
              <div key={t.id} data-testid={t.testId}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
